























































































































import { Vue, Component, Watch } from "vue-property-decorator";

import router from "@/router/router";

import DeviceTemplateItem from "./DeviceTemplateItem.vue";
import { ToolbarContentModule } from "@/store/modules/ToolbarContent";
import { DevicesModule } from "@/store/modules/DevicesModule";
import { 
  getCategoryIdFromSerialNumber, 
  getUpdatedCategoryAndTemplateIds, 
  getTemplateIdFromSerialNumber, 
  isClientPanel, 
  isIresCategory, 
  isIresTemplate 
} from "@/data/models/DeviceSettingsManager";
import { findBranch, hasClientEnabledStrictBranchFilters, shouldHideSerialNumber } from "@/data/repository/ClientRepository";
import { BranchFilter } from "@/data/models/BranchFilter";
import { GenericDeviceFilter, getGenericFilterName, matchesGenericDeviceFilter } from "@/data/models/DeviceFilter";
import { DeviceOverview } from "@/data/models/DeviceOverview";
import ClientPanel from "@/data/models/ClientPanel";
import { getDefaultBranchFilterOfUser } from "@/data/repository/UserRepository";
import { getBranchName } from "@/data/models/Branch";
import BranchFilterSelectorComponent from "../common/BranchFilterSelector.vue";
import InfiniteLoading, { StateChanger } from "vue-infinite-loading";
import MenuIconWithSelectionListComponent from "../common/FilterMenu.vue";
import SearchForDeviceComponent from "../devices/SearchForDevice.vue";
import { getNumber, setNumber } from "@/utils/SecureLocalStorage";
import { filterRetainedDevices, getNumberOfRetainedDevices } from "@/utils/DeviceManagementUtils";
import { observeDeviceOverviews } from "@/data/repository/AllDevicesRepository";
import { observeClientPanels } from "@/data/repository/ClientPanelRepository";
import { defineCategoriesAndTemplatesClientAccess } from "@/permissions/ClientPermissions";
import { DeviceCategoriesAndTemplatesModule } from "@/store/modules/DeviceCategoriesAndTemplatesModule";
import { calculateBackgroundStyle, setLastVisitedItem, StorageKeyLastVisitedItemLevel } from "@/utils/Utils";
import { COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK } from "@/utils/colors";
import { themed } from "@/utils/Theme";
import { getStoredBranchFilterId, setStoredBranchFilterId, removeStoredBranchFilterId } from "@/utils/BranchFilterStorage";

const ALL_DEVICES_TEMPLATE_ID = "ALL_DEVICES_TEMPLATE_ID";

@Component({
  components: {
    "branch-filter-selector": BranchFilterSelectorComponent,
    "category-template-item": DeviceTemplateItem,
    "infinite-loading": InfiniteLoading,
    "menu-icon": MenuIconWithSelectionListComponent,
    "search-for-device": SearchForDeviceComponent,
  },
})

export default class DeviceTemplateComponent extends Vue {
  allDevices: ClientPanel[] = [];
  allFilters = [
    GenericDeviceFilter.ALL_DEVICES,
    GenericDeviceFilter.IN_USAGE,
    GenericDeviceFilter.AVAILABLE,
    GenericDeviceFilter.NOT_AVAILABLE,
  ];
  categoryId = "";
  componentActive = false;
  hasNoDevices = true;
  infiniteId = new Date();
  pageSize = 20;
  searchQuery: string | null = null;
  searchTimeout: NodeJS.Timeout | null = null;
  selectedBranchFilter = getDefaultBranchFilterOfUser();
  selectedDeviceFilter = GenericDeviceFilter.ALL_DEVICES;
  sliceLength = this.pageSize;
  storageKeyAllDevicesFilter = "storageKeyAllDevicesFilter";
  storageKeyBranchFilter = "AllDevicesBranchFilter";
  storageKeySliceLength = "devicesTemplateStorageKeySliceLength";
  subscriptions: Array<() => void> = [];
  windowWidth = 0;

  retrievedClientPanels: ClientPanel[] = [];
  retrieveDeviceOverviews: DeviceOverview[] = [];

  get tableHeaderColor() {
    return themed(COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK);
  }

  get allDevicesTemplateId() {
    return ALL_DEVICES_TEMPLATE_ID;
  }

  get clientPanels() {
    return this.retrievedClientPanels;
  }

  get deviceFilterIconRes() {
    return "mdi-filter-variant";
  }

  get deviceOverviews() {
    return this.retrieveDeviceOverviews;
  }

  /**
   * Iterates over each device overview, updating the category and template IDs based on shared device settings.
   * This method leverages the `getUpdatedCategoryAndTemplateIds` function to find the updated category and template IDs
   * from the shared device settings that correspond to the local settings for each device. It then updates these IDs
   * in the device overview list.
   *
   * @returns {DeviceOverview[]} An array of device overview objects with updated category and template IDs.
   */
  get deviceOverviewsWithUpdatedCategoryAndTemplateIds() {
    const deviceOverviews = this.deviceOverviews;

    deviceOverviews.forEach((device) => {
      const { updatedCategoryId, updatedTemplateId } = getUpdatedCategoryAndTemplateIds(
        device.categoryId,
        device.templateId,
        this.localDeviceSettingsJson,
        this.sharedDeviceSettingsJson,
        device.devicePowerUsageType ?? undefined,
      );

      device.categoryId = updatedCategoryId;
      device.templateId = updatedTemplateId;
    });

    return deviceOverviews;
  }

  get selectedCategory() {
    return DevicesModule.selectedCategories;
  }

  get deviceTemplates() {
    return this.selectedCategory.length > 0
      ? this.selectedCategory[0].deviceTemplates
      : []
  }

  get isDefaultBranchFilterSelected() {
    return this.selectedBranchFilter.isDefaultBranchFilter();
  }

  get isDefaultDeviceFilterSelected() {
    return this.selectedDeviceFilter === GenericDeviceFilter.ALL_DEVICES;
  }

  get templatesToDisplay() {
    return this.sliceTemplatesToDisplay(this.sliceLength);
  }

  get showInfiniteLoading() {
    return this.deviceTemplates.length > 0;
  }

  get isAllowedToDefineOwnDeviceCategoriesAndTemplates() {
    return defineCategoriesAndTemplatesClientAccess();
  }

  get sharedDeviceSettingsJson() {
    return DeviceCategoriesAndTemplatesModule.sharedDeviceCategoriesAndTemplates;
  }

  get localDeviceSettingsJson() {
    return DeviceCategoriesAndTemplatesModule.localDeviceCategoriesAndTemplates;
  }

  get selectedDeviceFiltersDescription() {
    let filtersName = this.$t("allDevices.title").toString();
    let isFilterEnabled = false;

    // Might use later
    /* const selectedClientFilterName = this.selectedClientFilter.client
      ? getClientName(this.selectedClientFilter.client)
      : this.$t("common.all");

    if (this.isAllowedToViewClientSelector && selectedClientFilterName !== this.$t("common.all")) {
      filtersName += " (" + selectedClientFilterName;
      isFilterEnabled = true;
    } */

    const selectedBranchFilterName = this.selectedBranchFilter.branch
      ? getBranchName(this.selectedBranchFilter.branch)
      : this.$t("common.all");

    if (selectedBranchFilterName !== this.$t("common.all")) {
      filtersName += !isFilterEnabled
        ? " ("
        : "";
      filtersName += /* (this.isAllowedToViewClientSelector
        ? ": "
        : "") +  */selectedBranchFilterName;
      isFilterEnabled = true;
    }

    return filtersName + (isFilterEnabled ? ")" : "");
  }

  get shouldHideSerialNumber() {
    return shouldHideSerialNumber();
  }

  @Watch("selectedBranchFilter")
  onBranchFilterChanged() {
    this.resetDisplayedTemplates();
    this.storeBranch();
  }

  @Watch("selectedDeviceFilter")
  onFilterChanged(newFilter: GenericDeviceFilter) {
    this.setToolbarTitle(newFilter);
    this.resetDisplayedTemplates();

    setNumber(this.storageKeyAllDevicesFilter, this.selectedDeviceFilter);
  }
  
  clearSearchQuery() {
    this.searchQuery = null;
    this.resetDisplayedTemplates();
  }

  debounceSearch(newQuery: string) {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    if (newQuery === "") {
      this.clearSearchQuery();
      return;
    }

    this.searchTimeout = setTimeout(() => {
      this.processSearchQuery(newQuery);
    }, 400);
  }

  filterDevices(devices: (DeviceOverview | ClientPanel)[]) {
    const filter = this.selectedDeviceFilter;
    const branchFilter = this.selectedBranchFilter;
    const searchQuery = this.searchQuery?.toUpperCase();

    const result = devices.filter((device: DeviceOverview | ClientPanel) =>
      (
        branchFilter.doesMatchGenericDevice(device) ||
        this.isDefaultBranchFilterSelected
      ) &&
      matchesGenericDeviceFilter(
        filter,
        device
      )
    );

    if (searchQuery) {
      const searchQueryLowerCase = searchQuery.toLowerCase();

      const searchedDevices = result.filter(
        (device: DeviceOverview | ClientPanel) => {
          if (isClientPanel(device)) {
            const panel = device as ClientPanel;

            return (
                panel.serialNumber
                  .toLowerCase()
                  .includes(searchQueryLowerCase) &&
                !this.shouldHideSerialNumber
              ) ||
              panel.inventoryNumber
                ?.toLowerCase()
                .includes(searchQueryLowerCase)
          }
          else {
            const deviceOverview = device as DeviceOverview;

            return deviceOverview.serialNumber
                ?.toLowerCase()
                .includes(searchQueryLowerCase) ||
              deviceOverview.qrCode
                ?.toLowerCase()
                .includes(searchQueryLowerCase) ||
              deviceOverview.inventoryNumber
                ?.toLowerCase()
                .includes(searchQueryLowerCase)
          }
        });

        if (this.hasNoDevices !== false) {
          this.hasNoDevices = searchedDevices.length === 0;
        }

        return searchedDevices;    
    }

    const retainedDevices = filterRetainedDevices(result);

    if (this.hasNoDevices !== false) {
      this.hasNoDevices = retainedDevices.length === 0;
    }

    return retainedDevices;
  }

  getAllFiltersName() {
    let filtersName = this.getGenericFilterName(this.selectedDeviceFilter);

    const selectedBranchFilterName = this.selectedBranchFilter.branch
      ? getBranchName(this.selectedBranchFilter.branch)
      : this.$t("common.all");

    if (selectedBranchFilterName !== this.$t("common.all")) {
      filtersName += ", " + selectedBranchFilterName;
    }

    return filtersName;
  }

  calculateBackgroundStyle(templateId: string) {
    const categoryId = this.categoryId;
    const itemId = `${categoryId}/${templateId}`;

    return calculateBackgroundStyle(itemId, StorageKeyLastVisitedItemLevel.LEVEL_2);
  }

  getDevicesWithCategory(deviceCategoryId: string) {
    if (isIresCategory(deviceCategoryId)) {
      return this.clientPanels.filter((panel) => {
        return getCategoryIdFromSerialNumber(panel.serialNumber) === deviceCategoryId;
      });
    }

    const deviceOverviews = this.deviceOverviewsWithUpdatedCategoryAndTemplateIds;
    const devicesFilteredByCategory = deviceOverviews.filter((device) => device.categoryId === deviceCategoryId);

    return devicesFilteredByCategory;
  }

  getDevicesWithTemplate(deviceTemplateId: string) {
    if (isIresTemplate(deviceTemplateId)) {
      return this.clientPanels.filter((panel) => {
        return getTemplateIdFromSerialNumber(panel.serialNumber) === deviceTemplateId;
      });
    }

    const deviceOverviews = this.deviceOverviewsWithUpdatedCategoryAndTemplateIds;
    const devicesFilteredByTemplate = deviceOverviews.filter((device) => device.templateId === deviceTemplateId);

    return devicesFilteredByTemplate;
  }

  getGenericFilterName(deviceFilter: GenericDeviceFilter) {
    return getGenericFilterName(this, deviceFilter);
  }

  getNoOfTotalDevices(devices: (DeviceOverview | ClientPanel)[]) {
    return getNumberOfRetainedDevices(devices);
  }

  onOpenAllDeviceTemplates(shouldFocusSearch?: boolean) {
    const categoryId = this.categoryId;
    DevicesModule.setSelectedCategories(this.selectedCategory);

    setLastVisitedItem(
      `${categoryId}/${ALL_DEVICES_TEMPLATE_ID}`,
      StorageKeyLastVisitedItemLevel.LEVEL_2
    );

    router.push({
      path: `all/`,
      query: shouldFocusSearch ? {
        search: "true"
      } : {}
    });
  }

  onOpenDeviceTemplate(templateId: string) {
    const categoryId = this.categoryId;

    const selectedCategoryName = this.selectedCategoryName;
    const selectedTemplate = this.deviceTemplates.filter((template) => { return template.id === templateId });

    DevicesModule.setSelectedTemplates(selectedTemplate);

    setLastVisitedItem(
      `${categoryId}/${templateId}`,
      StorageKeyLastVisitedItemLevel.LEVEL_2
    );

    router.push({
      path: `${categoryId}/${templateId}/`,
      query: {
        selectedCategoryName: selectedCategoryName
      }
    });
  }

  processSearchQuery(newQuery: string) {
    this.searchQuery = newQuery;
    this.resetDisplayedTemplates();
  }

  resetDisplayedTemplates() {
    this.hasNoDevices = true;
    this.sliceLength = this.pageSize;
    this.infiniteId = new Date();
  }

  storeBranch() {
    if (this.selectedBranchFilter.branch?.id) {
      setStoredBranchFilterId(this.storageKeyBranchFilter, this.selectedBranchFilter.branch.id);
    }
    else {
      removeStoredBranchFilterId(this.storageKeyBranchFilter);
    }
  }

  get selectedCategoryName() {
    return this.selectedCategory[0]?.categoryName ?? "";
  }

  setToolbarTitle(newFilter: GenericDeviceFilter) {
    const toolbarTitle = this.$t("pages.allDevices").toString() + " > " + this.selectedCategoryName;

    const devicesString = this.selectedDeviceFilter === GenericDeviceFilter.ALL_DEVICES
      ? `${toolbarTitle}`
      : `${toolbarTitle} (${this.getGenericFilterName(newFilter)})`;

    ToolbarContentModule.setToolbarTitle(devicesString);
  }

  sliceTemplatesToDisplay(length: number) {
    const deviceTemplates = this.deviceTemplates;

    return deviceTemplates.slice(0, length);
  }

  activated() {
    this.componentActive = true;
    this.setToolbarTitle(this.selectedDeviceFilter);
  }
  
  beforeDestroy() {
    this.subscriptions.forEach((subscription) => subscription());
  }

  created() {
    this.categoryId = router.currentRoute.params["categoryId"];
    
    const cachedDeviceFilter =
      getNumber(this.storageKeyAllDevicesFilter) ?? GenericDeviceFilter.ALL_DEVICES;

    if (cachedDeviceFilter != null && cachedDeviceFilter !== undefined) {
      this.selectedDeviceFilter = cachedDeviceFilter;
    }

    this.subscriptions.push(observeDeviceOverviews(
      (deviceOverview: Array<DeviceOverview>) => {
        this.retrieveDeviceOverviews = deviceOverview;
      }
    ));
    this.subscriptions.push(observeClientPanels(
      (clientPanels: Array<ClientPanel>) => {
        this.retrievedClientPanels = clientPanels;
      }
    ));

    this.setToolbarTitle(this.selectedDeviceFilter);
  }

  deactivated() {
    this.componentActive = false;
  }

  mounted() {
    const branchIdQuery = getStoredBranchFilterId(this.storageKeyBranchFilter);

    if (branchIdQuery) {
      const matchingBranch = findBranch(branchIdQuery);

      if (matchingBranch) {
        this.selectedBranchFilter = new BranchFilter(
          hasClientEnabledStrictBranchFilters(),
          matchingBranch
        );
      }
    }

    this.componentActive = true;
  }

  async infiniteHandler(data: StateChanger) {
    const nextSliceLength = this.sliceLength + this.pageSize;
    const nextSlice = this.sliceTemplatesToDisplay(nextSliceLength);

    this.sliceLength = nextSliceLength;

    if (nextSliceLength > nextSlice.length) {
      data.complete();
    } else {
      data.loaded();
    }
  }
}
